<template>
  <div>
    <div
      class="modal fade overflow-hidden"
      id="mapModal"
      tabindex="-1"
      aria-labelledby="mapModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-body position-relative p-0">
            <!-- Map container -->
            <div
              id="map"
              ref="mapContainer"
              style="width: 100%; height:100vh; position: relative;"
            >
              <!-- Distance/Elevation label overlay -->
              <div v-if="showLabels" class="label-style">
                <div>Distance: {{ currentDistance }}</div>
                <div>Elevation: {{ currentElevation }}</div>
              </div>
            </div>
            <div v-if="mapInitialized" class="flyover-toolbar">
              <button class="btn btn-secondary flyover-pill-button" @click="openMapsDialog">
                Maps
              </button>

              <div class="speed-buttons">
                <button
                  v-for="speed in speedOptions"
                  :key="speed"
                  :class="['btn', 'flyover-pill-button', playbackSpeed === speed ? 'btn-primary' : 'btn-secondary']"
                  @click="setPlaybackSpeed(speed)"
                >
                  {{ speed }}x
                </button>
              </div>

              <div v-if="animationController" class="control-buttons">
                <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button" @click="handleRewind">
                  <span class="material-icons">fast_rewind</span>
                </button>

                <button class="btn btn-primary rounded-circle play-button d-flex align-items-center control-button" @click="togglePlayback">
                  <span v-if="isPlaying" class="material-icons">pause</span>
                  <span v-else class="material-icons">play_arrow</span>
                </button>

                <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button" @click="handleFastForward">
                  <span class="material-icons">fast_forward</span>
                </button>
              </div>
            </div>

            <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button close-button" data-bs-dismiss="modal">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>
      </div>
      <LoadingDialog :show-loading-dialog="loading"/>
    </div>
    <Teleport to="body">
      <BaseMapStylesDialog
        dialog-id="flyover-map-styles-dialog"
        map-instance-name="flyover_map"
        :maps-only="true"
        :show3-d-toggle="false"
        :force-full-style-reload="true"
      />
    </Teleport>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import $ from "jquery";
// Import the previewTrail helper (ensure the path is correct)
import { previewTrail } from "../../utils/helpers/TrailPreview";

// GraphQL clients/queries
import graph_client from "../../utils/graphql/graph_client";
import { GetHike } from "../../utils/graphql/hikes-graph";
import { GetPtv2PlannedTrailLineString } from "../../utils/graphql/planned-trail-graph";
import { GetRecordedTrailLineStringById } from "../../utils/graphql/recorded-trail-graph";
import { MeasurementPrefUtility } from "../../utils/helpers/MeasurementPref";
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import { Modal } from "bootstrap";
import BaseMapStylesDialog from "../base_map_dialog/BaseMapStylesDialog.vue";
import { HiikerMapStyle } from "../../utils/models/MapStyles";
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";

export default {
  name: "FlyoverPreview",
  components: {LoadingDialog, BaseMapStylesDialog},
  props: {
    title: { type: String, default: "3D Preview" }
  },
  data() {
    return {
      loading: false,
      mapInitialized: false,
      defaultCenter: [-74.5, 40],
      featureCollection: null,
      segments: [], // Array of linestring segments
      currentSegmentIndex: 0,
      segmentControllers: [], // Animation controllers for each segment
      preloadedSegments: new Map(), // Cache for preloaded segment data
      mapboxToken:
        "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2xydDBwMTU4MDFmdDJwbzd5NjFxMmlyciJ9.Q0bXbghB7N2Ji93oLrAJng",
      currentDistance: 0,
      currentElevation: 0,
      showLabels: true,
      animationController: null,
      isPlaying: true,
      finishedAnimation: false,
      playbackSpeed: 1,
      speedOptions: [1, 2, 3, 4, 5, 10],
      controllerSequence: 0,
      activeControllerId: null,
    };
  },
  computed: {
    labelStyle() {
      return {
        position: "absolute",
        top: "1em",
        left: "1em",
        zIndex: 999,
        background: "rgba(23, 24, 29, 0.7)",
        color: "#fff",
        padding: "8px 12px",
        borderRadius: "4px",
        fontSize: "16px",
        pointerEvents: "none"
      };
    }
  },
  mounted() {
    this.$store.commit("mapStyles/setCurrentMapStyle", HiikerMapStyle.satellite());

    // Keep the same global function name for backward compatibility
    // Now it loads Hike data instead of Trail
    window.showMapModalForTrail = trail_id => {
      this.showModal();
      this.loading = true;
      graph_client.request(GetHike, { id: trail_id }).then(data => {
        // Use routes-only for flyover preview to show just the main route
        const hike = data.hike;
        if (hike && hike.geojsonRoutesOnlyString) {
          const fc = JSON.parse(hike.geojsonRoutesOnlyString);
          // Collect all MultiLineString geometries and split them into LineString segments
          this.segments = [];
          const multiLineFeatures = (fc.features || []).filter(f => f.geometry && f.geometry.type === "MultiLineString");
          multiLineFeatures.forEach((f) => {
            this.segments.push(...this.parseMultilinestring(f.geometry));
          });
          // If no MultiLineString features found, use the feature collection as-is
          if (this.segments.length === 0) {
            this.featureCollection = fc;
          } else {
            this.featureCollection = fc; // keep as context for bounds/etc
          }
          this.initializeMap();
        } else {
          // As a last resort, just center the map
          this.featureCollection = null;
          this.segments = [];
          this.initializeMap();
        }
      });
    };

    window.showMapModalForPlannedTrail = planned_trail_id => {
      this.showModal();
      this.loading = true;
      graph_client
        .request(GetPtv2PlannedTrailLineString, { id: planned_trail_id })
        .then(data => {
          this.featureCollection = JSON.parse(
            data.ptv2PlannedTrailById.lineStringFeatureCollection
          );
          this.initializeMap();
        });
    };

    window.showMapModalForRecordedTrail = recorded_trail_id => {
      this.showModal();
      this.loading = true;
      graph_client
        .request(GetRecordedTrailLineStringById, { input: recorded_trail_id })
        .then(data => {
          this.featureCollection = JSON.parse(data.recording.lineString);
          this.initializeMap();
        });
    };

    // Accept raw GeoJSON FeatureCollection directly (used by plan trail)
    window.showMapModalForCoordinates = (featureCollection) => {
      this.showModal();
      this.loading = true;
      this.segments = [];

      if (typeof featureCollection === "string") {
        featureCollection = JSON.parse(featureCollection);
      }

      // Check for MultiLineString features and split into segments
      const multiLineFeatures = (featureCollection.features || []).filter(
        f => f.geometry && f.geometry.type === "MultiLineString"
      );
      if (multiLineFeatures.length > 0) {
        multiLineFeatures.forEach((f) => {
          this.segments.push(...this.parseMultilinestring(f.geometry));
        });
      }

      // Also collect LineString features as segments
      const lineStringFeatures = (featureCollection.features || []).filter(
        f => f.geometry && f.geometry.type === "LineString"
      );
      if (lineStringFeatures.length > 0) {
        lineStringFeatures.forEach((f, index) => {
          this.segments.push({
            ...f,
            properties: { ...f.properties, segmentIndex: this.segments.length + index }
          });
        });
      }

      this.featureCollection = featureCollection;
      this.initializeMap();
    };

    $("#mapModal").on("shown.bs.modal", () => {
      if (window.flyover_map) {
        window.flyover_map.resize();
      }
    });

    $("#mapModal").on("hidden.bs.modal", () => {
      this.resetAnimationControllers();
      this.segments = [];
      this.currentSegmentIndex = 0;
      this.preloadedSegments.clear();
      if (window.flyover_map) {
        window.flyover_map.remove();
        window.flyover_map = null;
      }
      this.mapInitialized = false;
      this.currentDistance = 0;
      this.currentElevation = 0;
      this.finishedAnimation = false;
      this.isPlaying = true;
      this.playbackSpeed = 1;
    });
  },
  beforeUnmount() {
    $("#mapModal").off("shown.bs.modal");
    $("#mapModal").off("hidden.bs.modal");
    delete window.showMapModalForRecordedTrail;
    delete window.showMapModalForPlannedTrail;
    delete window.showMapModalForTrail;
    delete window.showMapModalForCoordinates;
    this.resetAnimationControllers();
    if (window.flyover_map) {
      window.flyover_map.remove();
      window.flyover_map = null;
    }
  },
  methods: {
    showModal() {
      const element = document.getElementById("mapModal");
      const modal = new Modal(element);
      modal.show();

      // Track mixpanel event for 3D preview (support both Hike/Trail naming during migration)
      if (window.mixpanel) {
        const name = window.hike_name || window.trail_name || window.trailName;
        const id = window.hike_id || window.trail_id || window.trailId || window.id;
        window.mixpanel.track('3D Preview Shown', {
          'Hike Name': name,
          'Hike ID': id,
          'Trail Name': name,
          'Trail ID': id
        });
      }
    },
    createControllerId() {
      this.controllerSequence += 1;
      return this.controllerSequence;
    },
    destroyAnimationController(controller) {
      if (!controller) return;
      if (typeof controller.destroy === "function") {
        controller.destroy();
        return;
      }
      if (typeof controller.stop === "function") {
        controller.stop();
      }
    },
    resetAnimationControllers() {
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);
      this.animationController = null;
      this.segmentControllers.forEach(controller => {
        this.destroyAnimationController(controller);
      });
      this.segmentControllers = [];
    },
    applyPlaybackSpeed(controller = this.animationController) {
      if (!controller || typeof controller.setSpeedMultiplier !== "function") return;
      controller.setSpeedMultiplier(this.playbackSpeed);
    },
    setPlaybackSpeed(speed) {
      this.playbackSpeed = speed;
      this.applyPlaybackSpeed();
    },
    openMapsDialog() {
      if (window.user) {
        const mapStylesDialog = document.getElementById("flyover-map-styles-dialog");
        if (mapStylesDialog) {
          Modal.getOrCreateInstance(mapStylesDialog).show();
        }
      } else {
        window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
      }
    },
    currentMapStyleUrl() {
      return this.$store.state.mapStyles.currentMapStyle?.styleUrl || HiikerMapStyle.satellite().styleUrl;
    },
    parseMultilinestring(multilinestring) {
      // Parse GeoJSON MultiLineString into individual LineString segments
      const segments = [];

      if (multilinestring.type === "MultiLineString") {
        // Direct MultiLineString geometry
        multilinestring.coordinates.forEach((coords, index) => {
          segments.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coords
            },
            properties: {
              segmentIndex: index
            }
          });
        });
      } else if (multilinestring.type === "FeatureCollection") {
        // FeatureCollection containing LineStrings
        multilinestring.features.forEach((feature, index) => {
          if (feature.geometry.type === "LineString") {
            segments.push({
              ...feature,
              properties: {
                ...feature.properties,
                segmentIndex: index
              }
            });
          }
        });
      } else if (multilinestring.type === "Feature" && multilinestring.geometry.type === "MultiLineString") {
        // Feature with MultiLineString geometry
        multilinestring.geometry.coordinates.forEach((coords, index) => {
          segments.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coords
            },
            properties: {
              segmentIndex: index
            }
          });
        });
      }

      return segments;
    },
    initializeMap() {
      if (this.mapInitialized) return;
      mapboxgl.accessToken = this.mapboxToken;
      window.flyover_map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: this.currentMapStyleUrl(),
        zoom: 5,
        pitch: 60,
        bearing: 0,
        antialias: true,
        // Support both historic window.lonlat and newer window.lonLat keys
        center: (window.lonlat || window.lonLat || [0.0, 0.0]),
      });
      window.flyover_map.on("style.load", () => {
        if (!window.flyover_map.getSource("mapbox-dem")) {
          window.flyover_map.addSource("mapbox-dem", {
            type: "raster-dem",
            url: "mapbox://mapbox.mapbox-terrain-dem-v1",
            tileSize: 512,
            maxzoom: 14
          });
        }
        window.flyover_map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });
        this.renderFeatureCollection();
        window.flyover_map.resize();
      });
      window.flyover_map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
      this.mapInitialized = true;
    },
    clearLayers() {
      if (!window.flyover_map) return;
      if (window.flyover_map.getLayer("feature-shadow")) {
        window.flyover_map.removeLayer("feature-shadow");
      }
      if (window.flyover_map.getLayer("feature")) {
        window.flyover_map.removeLayer("feature");
      }
      if (window.flyover_map.getSource("feature-data")) {
        window.flyover_map.removeSource("feature-data");
      }
      if (window.flyover_map.getLayer("progressTrail")) {
        window.flyover_map.removeLayer("progressTrail");
      }
      if (window.flyover_map.getSource("progressTrail")) {
        window.flyover_map.removeSource("progressTrail");
      }
    },
    renderFeatureCollection() {
      this.loading = false;
      if (!window.flyover_map) return;

      // Handle new segment-based rendering
      if (this.segments && this.segments.length > 0) {
        this.renderSegmentedTrail();
        return;
      }

      // Fallback to old rendering for non-segmented trails
      if (!this.featureCollection) {
        const center = window.lonLat || this.defaultCenter;
        window.flyover_map.flyTo({
          center: center,
          zoom: 12,
          pitch: 60,
          bearing: 0
        });
        return;
      }
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);
      this.animationController = null;
      this.clearLayers();
      let geojson, coordinates = [];
      if (typeof this.featureCollection === "string") {
        try {
          geojson = JSON.parse(this.featureCollection);
        } catch (e) {
          console.error("Error parsing feature collection:", e);
          return;
        }
      } else {
        geojson = this.featureCollection;
      }
      if (geojson.type === "FeatureCollection") {
        for (const feature of geojson.features) {
          if (feature.geometry && feature.geometry.coordinates) {
            if (feature.geometry.type === "LineString") {
              coordinates = feature.geometry.coordinates;
              break;
            } else if (feature.geometry.type === "Point") {
              coordinates = [feature.geometry.coordinates];
              break;
            } else if (feature.geometry.type === "Polygon") {
              coordinates = feature.geometry.coordinates[0];
              break;
            }
          }
        }
      } else if (geojson.type === "Feature") {
        if (geojson.geometry && geojson.geometry.coordinates) {
          if (geojson.geometry.type === "LineString") {
            coordinates = geojson.geometry.coordinates;
          } else if (geojson.geometry.type === "Point") {
            coordinates = [geojson.geometry.coordinates];
          } else if (geojson.geometry.type === "Polygon") {
            coordinates = geojson.geometry.coordinates[0];
          }
        }
      }
      window.flyover_map.addSource("feature-data", { type: "geojson", data: geojson });
      const features = geojson.features || [geojson];
      const feature = features[0];
      const geometryType = feature.geometry ? feature.geometry.type : null;
      window.flyover_map.addLayer({
        id: "feature-shadow",
        type: geometryType === "Point" ? "circle" : "line",
        source: "feature-data",
        paint:
          geometryType === "Point"
            ? { "circle-radius": 12, "circle-color": "#000", "circle-opacity": 0.4, "circle-blur": 3 }
            : { "line-color": "#DB2B35", "line-width": 4 }
      });
      window.flyover_map.addLayer({
        id: "feature",
        type: geometryType === "Point" ? "circle" : "line",
        source: "feature-data",
        paint:
          geometryType === "Point"
            ? { "circle-radius": 8, "circle-color": "#FF4500", "circle-opacity": 1 }
            : { "line-color": "#DB2B35", "line-width": 8, "line-opacity": 1 }
      });
      if (geometryType === "LineString" && typeof previewTrail === "function" && coordinates.length > 0) {
        const controllerId = this.createControllerId();
        this.animationController = previewTrail(
          window.flyover_map,
          coordinates,
          "medium",
          {},
          (distKm, elevM) => {
            const convertedDistance = MeasurementPrefUtility.preference().bigUnitFormatter(distKm);
            const convertedElv = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
            this.currentDistance = convertedDistance;
            this.currentElevation = convertedElv;
          }
        );
        this.activeControllerId = controllerId;
        this.applyPlaybackSpeed();
        this.isPlaying = true;
        this.finishedAnimation = false;
        this.animationController.then(() => {
          if (this.activeControllerId !== controllerId) return;
          this.isPlaying = false;
          this.finishedAnimation = true;
        });
      } else {
        this.fitToCoordinates(coordinates);
      }
    },
    fitToCoordinates(coordinates) {
      if (!coordinates || coordinates.length === 0 || !window.flyover_map) return;
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach(coord => bounds.extend(coord));
      window.flyover_map.fitBounds(bounds, { padding: 50, maxZoom: 15, pitch: 60 });
    },
    renderSegmentedTrail() {
      this.currentSegmentIndex = 0;
      this.resetAnimationControllers();
      this.clearLayers();

      // Add source and layers for the full trail visualization without connecting segment gaps
      const multiLineCoordinates = this.segments.map(seg => seg.geometry.coordinates);

      const fullTrailFeature = {
        type: "Feature",
        geometry: {
          type: "MultiLineString",
          coordinates: multiLineCoordinates
        }
      };

      window.flyover_map.addSource("feature-data", {
        type: "geojson",
        data: fullTrailFeature
      });

      window.flyover_map.addLayer({
        id: "feature-shadow",
        type: "line",
        source: "feature-data",
        paint: { "line-color": "#DB2B35", "line-width": 4 }
      });

      window.flyover_map.addLayer({
        id: "feature",
        type: "line",
        source: "feature-data",
        paint: { "line-color": "#DB2B35", "line-width": 8, "line-opacity": 1 }
      });

      // Relive: a single flyover over the WHOLE route (flatten all segments) instead of playing each
      // segment separately, so the flat-2D->3D intro, the orbiting follow, and the 2D zoom-out outro
      // frame the entire hike as one experience.
      const allCoords = this.segments.flatMap(seg => seg.geometry.coordinates);
      if (allCoords.length < 2) {
        this.finishedAnimation = true;
        this.isPlaying = false;
        return;
      }

      const controllerId = this.createControllerId();
      this.animationController = previewTrail(
        window.flyover_map,
        allCoords,
        "medium",
        {},
        (distKm, elevM) => {
          this.currentDistance = MeasurementPrefUtility.preference().bigUnitFormatter(distKm);
          this.currentElevation = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
        }
      );
      this.activeControllerId = controllerId;
      this.applyPlaybackSpeed();
      this.isPlaying = true;
      this.finishedAnimation = false;
      this.animationController.then(() => {
        if (this.activeControllerId !== controllerId) return;
        this.isPlaying = false;
        this.finishedAnimation = true;
      });
    },
    playSegment(segmentIndex, startFromEnd = false) {
      if (segmentIndex >= this.segments.length) {
        this.finishedAnimation = true;
        this.isPlaying = false;
        return;
      }

      this.currentSegmentIndex = segmentIndex;
      const segment = this.segments[segmentIndex];

      // Get previous segment's ending state for smooth transition
      let previousEndState = null;
      if (segmentIndex > 0 && this.segmentControllers[segmentIndex - 1] && !startFromEnd) {
        previousEndState = this.segmentControllers[segmentIndex - 1].getEndState();
      }

      // If starting from end (rewinding from next segment), get next segment's starting state
      let nextStartState = null;
      if (startFromEnd && segmentIndex < this.segments.length - 1 && this.segmentControllers[segmentIndex + 1]) {
        nextStartState = this.segmentControllers[segmentIndex + 1].getStartState ?
          this.segmentControllers[segmentIndex + 1].getStartState() : null;
      }

      // Create animation controller for this segment
      const cameraOptions = {};

      if (startFromEnd && nextStartState) {
        // Starting from end when rewinding from next segment
        cameraOptions.initialBearing = nextStartState.bearing;
        cameraOptions.initialPitch = nextStartState.pitch;
        cameraOptions.startFromEnd = true;
      } else if (previousEndState) {
        // Normal forward progression
        cameraOptions.initialBearing = previousEndState.bearing;
        cameraOptions.initialPitch = previousEndState.pitch;
      }

      const controllerId = this.createControllerId();
      this.animationController = previewTrail(
        window.flyover_map,
        segment.geometry.coordinates,
        "medium",
        {},
        (distKm, elevM) => {
          // Calculate total distance including previous segments
          let totalDistance = distKm;
          for (let i = 0; i < segmentIndex; i++) {
            const prevSegment = this.segments[i];
            const prevLength = this.calculateSegmentLength(prevSegment.geometry.coordinates);
            totalDistance += prevLength;
          }

          const convertedDistance = MeasurementPrefUtility.preference().bigUnitFormatter(totalDistance);
          const convertedElv = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
          this.currentDistance = convertedDistance;
          this.currentElevation = convertedElv;
        },
        cameraOptions
      );

      this.activeControllerId = controllerId;
      this.segmentControllers[segmentIndex] = this.animationController;
      this.applyPlaybackSpeed();
      this.isPlaying = true;
      this.finishedAnimation = false;

      // When segment finishes, play the next one
      this.animationController.then(() => {
        if (this.activeControllerId !== controllerId) return;
        const isLastSegment = segmentIndex >= this.segments.length - 1;

        if (this.isPlaying) {
          if (!isLastSegment) {
            this.destroyAnimationController(this.segmentControllers[segmentIndex]);
            this.playSegment(segmentIndex + 1);

            if (segmentIndex + 2 < this.segments.length) {
              this.preloadSegment(segmentIndex + 2);
            }
          } else {
            this.finishedAnimation = true;
            this.isPlaying = false;
          }
        } else {
          if (!isLastSegment) {
            this.currentSegmentIndex = segmentIndex + 1;
            this.animationController = null;
          } else {
            this.finishedAnimation = true;
          }
        }
      });
    },
    preloadSegment(segmentIndex) {
      // Preprocess segment data in background
      if (segmentIndex < this.segments.length) {
        const segment = this.segments[segmentIndex];
        // Store preprocessed data for faster loading
        this.preloadedSegments.set(segmentIndex, {
          coordinates: segment.geometry.coordinates,
          preprocessed: true
        });
      }
    },
    calculateSegmentLength(coordinates) {
      let totalLength = 0;
      for (let i = 1; i < coordinates.length; i++) {
        const p1 = coordinates[i - 1];
        const p2 = coordinates[i];
        const R = 6371; // Earth radius in km
        const dLat = (p2[1] - p1[1]) * Math.PI / 180;
        const dLon = (p2[0] - p1[0]) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(p1[1] * Math.PI / 180) * Math.cos(p2[1] * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        totalLength += R * c;
      }
      return totalLength;
    },
    jumpToSegment(segmentIndex, startFromEnd = false) {
      if (segmentIndex < 0 || segmentIndex >= this.segments.length) return;

      // Stop current animation
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);

      // Clear any pending segment loads
      this.segmentControllers.forEach((controller, index) => {
        if (controller && index !== segmentIndex) {
          this.destroyAnimationController(controller);
        }
      });

      // Play the target segment
      this.playSegment(segmentIndex, startFromEnd);

      // Preload adjacent segments
      if (segmentIndex > 0) {
        this.preloadSegment(segmentIndex - 1);
      }
      if (segmentIndex < this.segments.length - 1) {
        this.preloadSegment(segmentIndex + 1);
      }
    },
    handleFastForward() {
      if (!this.animationController) return;

      // Check if we're near the end of current segment
      if (this.segments && this.segments.length > 0) {
        // Try to fast forward within current segment
        this.animationController.fastForward();

        // If at end of current segment and there's a next segment, jump to it
        // This would require checking animation progress, which we'll handle in the animation callback
      } else {
        // Use regular fast forward for non-segmented trails
        this.animationController.fastForward();
      }
    },
    handleRewind() {
      if (!this.animationController) return;

      if (this.segments && this.segments.length > 0) {
        // Try to rewind within current segment
        this.animationController.rewind();

        // If at beginning of current segment and there's a previous segment
        // we could implement jumping to previous segment here
      } else {
        // Use regular rewind for non-segmented trails
        this.animationController.rewind();
      }
    },
    togglePlayback() {
      const controller = this.animationController;

      if (!controller) {
        if (this.finishedAnimation) {
          this.restartAnimation();
        } else if (this.segments && this.segments.length > 0) {
          const nextIndex = Math.min(this.currentSegmentIndex, this.segments.length - 1);
          this.playSegment(nextIndex || 0);
          this.isPlaying = true;
          this.finishedAnimation = false;
        }
        return;
      }

      const progress = typeof controller.getProgress === 'function' ? controller.getProgress() : null;

      if (this.finishedAnimation && (progress === null || progress >= 1)) {
        this.restartAnimation();
        return;
      }

      if (this.isPlaying) {
        controller.pause();
        this.isPlaying = false;
        this.finishedAnimation = false;
      } else {
        if (progress !== null && progress >= 1) {
          this.restartAnimation();
        } else {
          controller.resume();
          this.isPlaying = true;
          this.finishedAnimation = false;
        }
      }
    },
    restartAnimation() {
      this.activeControllerId = null;
      this.renderFeatureCollection();
      this.finishedAnimation = false;
      this.isPlaying = true;
    },
  },
};
</script>

<style scoped>
.modal-fullscreen {
  width: 100vw;
  max-width: none;
  height: 100vh;
  margin: 0;
}
.modal-fullscreen .modal-content {
  height: 100vh;
  border: 0;
  border-radius: 0;
}
.close-button {
  position: absolute;
  top: 1em;
  right: 1em;
  z-index: 1000;
  width: 40px;
  height: 40px !important;
}
.play-button {
  position: relative;
  z-index: 1000;
}
.flyover-toolbar {
  position: absolute;
  top: 1em;
  right: 5.5em;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  max-width: calc(100% - 8.5em);
  z-index: 1000;
}
.speed-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.flyover-pill-button {
  min-height: 40px;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-weight: 600;
}
.control-buttons {
  display: flex;
  gap: 10px;
}
.control-button {
  width: 40px;
  height: 40px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}
.spinner-border {
  width: 4rem;
  height: 4rem;
}

.label-style{
  position: absolute;
  top: 1em;
  left: 1em;
  z-index: 999;
  background: rgba(23, 24, 29, 0.7);
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  pointer-events: none;

  @media (max-width: 768px) {
    top: unset;
    bottom: 3em;
    left: 10px;
  }
}

@media (max-width: 768px) {
  .flyover-toolbar {
    top: 4.5em;
    left: 10px;
    right: 60px;
    max-width: none;
  }
}
</style>
